import { Component } from '@theme/component';

/**
 * Enhances native details navigation. The drawer is nonmodal: normal page tab order is retained.
 * @typedef {{ drawer: HTMLDetailsElement, toggle: HTMLElement }} NavigationRefs
 * @extends {Component<NavigationRefs>}
 */
class BroxNavigation extends Component {
  requiredRefs = ['drawer', 'toggle'];
  #abort;

  connectedCallback() {
    super.connectedCallback();
    this.#bind();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bind();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  #bind() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const { drawer, toggle } = this.refs;
    const sync = () => toggle.setAttribute('aria-expanded', String(drawer.open));
    sync();
    drawer.addEventListener('toggle', sync, { signal });
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const submenu = event.target instanceof Element ? event.target.closest('.brox-navigation__submenu[open]') : null;
      if (submenu instanceof HTMLDetailsElement) {
        submenu.open = false;
        submenu.querySelector('summary')?.focus();
      } else if (drawer.open) {
        drawer.open = false;
        sync();
        toggle.focus();
      }
    }, { signal });
    document.addEventListener('click', (event) => {
      if (drawer.open && event.target instanceof Node && !drawer.contains(event.target)) {
        drawer.open = false;
        sync();
      }
    }, { signal });
    drawer.addEventListener('click', (event) => {
      if (event.target instanceof Element && event.target.closest('a')) {
        drawer.open = false;
        sync();
      }
    }, { signal });
    const desktop = window.matchMedia('(min-width: 1200px)');
    desktop.addEventListener('change', () => {
      if (desktop.matches) { drawer.open = false; sync(); }
    }, { signal });
  }
}

if (!customElements.get('brox-navigation-component')) {
  customElements.define('brox-navigation-component', BroxNavigation);
}
